import { useEffect, useState } from 'react';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import { FacebookPixelsHead } from './FacebookTracker';
import { OpenAiPixelHead } from './OpenAiTracker';
import { LinkedInTrackerHead } from './LinkedInTracker';
import { PinterestTrackerHead } from './PinterestTracker';
import { OutbrainTrackerHead } from './Outbrain';
import {
    hasSaleOrSharingOptOut,
    shouldAllowThirdPartySharing,
} from './allow-third-party-sharing';
import {
    getAdvertisingWindow,
    releaseAdvertisingGate,
} from '../util/advertising-window';
import { hasSaleSharingOptOutCookie } from '@modules/privacy/sale-sharing-opt-out-cookie';

interface NavigatorWithGPC extends Navigator {
    globalPrivacyControl?: boolean;
}

/**
 * Boots the advertising trackers on the client only, after checking the Global
 * Privacy Control browser signal and the explicit sale/sharing opt-out from
 * either source: the signed-in member's preference or the opt-out cookie. The
 * matching `<noscript><img>` halves are SSR-rendered in `BodyPixels` from the
 * request headers, root data and cookie.
 *
 * Each child renders nothing and injects its vendor script from an effect via
 * `injectHeadScript`, which is the only way it runs: the browser does not
 * execute a `<script>` element React creates on the client.
 *
 * `allowAdvertising` starts false and resolves in `useEffect` so both signals
 * are read from the browser once after hydration (never during SSR), which
 * means the vendor scripts load one tick later than they would from
 * `HeadPixels`. Outbrain has no `<noscript>` fallback, so no-JS requests get no
 * Outbrain coverage; Facebook, Pinterest, and LinkedIn still emit
 * `<noscript><img>` pixels from `BodyPixels`.
 */
interface ClientAdvertisingPixelsProps extends Pick<
    ClientConfig['pixels'],
    | 'facebookPixelArchiproId'
    | 'facebookPixelClientId'
    | 'openAiPixelId'
    | 'pinterestTagArchiproId'
    | 'pinterestTagClientId'
    | 'linkedinPartnerId'
    | 'outbrainId'
> {
    /**
     * False on device-preview routes, which render the site inside a frame and
     * must not report traffic. This component still mounts, because it owns the
     * gate Facebook events wait on and something has to release it.
     */
    enabled: boolean;
}

export const ClientAdvertisingPixels = ({
    facebookPixelArchiproId,
    facebookPixelClientId,
    openAiPixelId,
    pinterestTagArchiproId,
    pinterestTagClientId,
    linkedinPartnerId,
    outbrainId,
    enabled,
}: ClientAdvertisingPixelsProps) => {
    const { localisation, user } = useRootData();
    const { honourGlobalPrivacyControl } = localisation.countryConfig;
    const memberOptedOut = hasSaleOrSharingOptOut(user);
    const [allowAdvertising, setAllowAdvertising] = useState(false);

    useEffect(() => {
        const gpcEnabled =
            (navigator as NavigatorWithGPC).globalPrivacyControl === true;
        // Both signals are read once per document. This component is mounted in
        // the root layout, so it does not remount on soft navigation and the
        // effect does not re-run; nothing here reacts to the opt-out cookie
        // being set mid-session. That is why the opt-out form reloads the page
        // on success rather than relying on a re-render.
        const allow =
            enabled &&
            shouldAllowThirdPartySharing(
                honourGlobalPrivacyControl,
                gpcEnabled,
                memberOptedOut || hasSaleSharingOptOutCookie(),
            );
        setAllowAdvertising(allow);

        // Suppressed sessions mount no pixels, so nothing downstream will open
        // the gate and every advertising event would wait forever.
        if (!allow) {
            releaseAdvertisingGate(getAdvertisingWindow());
        }
    }, [enabled, honourGlobalPrivacyControl, memberOptedOut]);

    // Parent effects run after their children's, so by the time this fires
    // every pixel below has booted. Releasing from one vendor's loader instead
    // would let the other vendors' events through before their globals exist.
    useEffect(() => {
        if (!allowAdvertising) return;
        releaseAdvertisingGate(getAdvertisingWindow());
    }, [allowAdvertising]);

    if (!allowAdvertising) {
        return null;
    }

    // Each loader skips ids that are unset or still the `foo` placeholder, so
    // the vendors that have nothing configured mount and no-op.
    return (
        <>
            <FacebookPixelsHead
                pixel={facebookPixelArchiproId}
                pixelClient={facebookPixelClientId}
            />
            <OpenAiPixelHead pixelId={openAiPixelId ?? ''} />
            <PinterestTrackerHead
                pixelArchipro={pinterestTagArchiproId}
                pixelClient={pinterestTagClientId}
            />
            <LinkedInTrackerHead partnerId={linkedinPartnerId ?? ''} />
            <OutbrainTrackerHead id={outbrainId ?? ''} />
        </>
    );
};
